<template>
  <div :class="wrapperClasses">
    <Tooltip
      ref="tooltipRef"
      v-bind="dynamicProps"
      :arrow="false"
      :placement="effectivePlacement"
      :trigger="triggerActions"
      transition-name="fade"
      :mouse-enter-delay="testMode ? 0 : props.mouseEnterDelay"
      :mouse-leave-delay="props.mouseLeaveDelay"
      :overlay-class-name="wrapperClasses"
      :get-popup-container="getPopupContainer"
      :auto-adjust-overflow="autoAdjustOverflow"
      :destroy-tooltip-on-hide="false"
      :align="effectiveAlign"
      class="partner-population-tooltips"
      @open-change="onVisibleChange"
    >
      <!-- default slot is for the item that triggers a popover -->
      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -- role is bound by triggerAria() -->
      <div
        ref="triggerRef"
        data-testid="bitts-tooltip-trigger"
        class="bitts-tooltip-trigger"
        :class="defaultSlotClasses"
        v-bind="triggerAria()"
        @keydown="onTriggerKeydown"
        @pointerdown="onPointerdown"
        @focusin="onFocusin"
        @mouseenter="onMouseenter"
        @mouseleave="onMouseleave"
      >
        <slot />
      </div>
      <!-- title slot is for tooltip content -->
      <template v-if="$slots.title" #title>
        <TestHook>
          <div
            :id="tooltipId"
            ref="titleRef"
            :role="includeCta || titleHasInteractive ? undefined : 'tooltip'"
          >
            <slot name="title" />
            <component :is="anchorOrDiv" v-if="includeCta" v-bind="anchorProps">
              <BittsButton
                :text="buttonText || 'Learn More'"
                type="white"
                :left-icon="ctaIcon"
                data-testid="bitts-tooltip-button"
                class="c-bitts-tooltip__button"
                size="x-small"
                @click="onCtaClicked($event)"
              />
            </component>
          </div>
        </TestHook>
      </template>
    </Tooltip>
  </div>
</template>

<script setup lang="ts">
import { useEventListener, useMutationObserver } from '@vueuse/core';
import { Tooltip } from 'ant-design-vue';
import {
  Comment,
  Fragment,
  Text,
  type VNode,
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  toRef,
  useId,
  useSlots,
  watch,
} from 'vue';

import BittsButton from '../BittsButton/BittsButton.vue';
import {
  AlignOverflow,
  useViewportPlacement,
  withViewportOverflow,
} from '../composables/useViewportPlacement';
import { FOCUSABLE_SELECTOR } from '../shared/focusableSelector';
import TestHook from '../TestHook.vue';
import { BittsIcon, Placement, PlacementType } from '../types';

export type TooltipAlign = {
  offset?: [number, number];
  overflow?: AlignOverflow;
};

export type Props = {
  overlayClass?: string;
  ctaIcon?: BittsIcon;
  buttonText?: string;
  includeCta?: boolean;
  placement?: PlacementType;
  learnMoreLink?: string | null;
  mountToBody?: boolean;
  showTooltip?: boolean | null;
  stopPropagation?: boolean;
  defaultSlotClasses?: string;
  mouseEnterDelay?: number;
  mouseLeaveDelay?: number;
  autoAdjustOverflow?: boolean;
  align?: TooltipAlign;
  // Set false when the tooltip only repeats what the trigger already shows, so
  // keyboard users are not given an extra tab stop for nothing.
  isTriggerFocusable?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  overlayClass: '',
  ctaIcon: null,
  buttonText: '',
  includeCta: false,
  placement: Placement.Top,
  learnMoreLink: null,
  mountToBody: false,
  showTooltip: null,
  stopPropagation: false,
  defaultSlotClasses: '',
  mouseEnterDelay: 0.5,
  mouseLeaveDelay: 0.1,
  autoAdjustOverflow: true,
  align: undefined,
  isTriggerFocusable: true,
});

const emit = defineEmits(['menu-visible', 'cta-clicked']);

const anchorOrDiv = computed(() => (props.learnMoreLink ? 'a' : 'div'));

const anchorProps = computed(() =>
  props.learnMoreLink
    ? {
        href: props.learnMoreLink,
        target: '_blank',
      }
    : {},
);

const wrapperClasses = computed(() => {
  const classes = ['bitts-tooltip'];
  if (props.overlayClass) classes.push(props.overlayClass);
  return classes.join(' ');
});

const testMode = computed(() => import.meta.env.MODE === 'test');

const tooltipRef = ref<{ getPopupDomNode(): Node | null } | null>(null);
const isSuppressed = ref(false);
const isAntdOpen = ref(false);

const triggerActions = ['hover'] as ('hover' | 'focus')[];
const tooltipId = useId();
const isFocusOpen = ref(false);
const escDismissed = ref(false);
const hoverDismissed = ref(false);
const controlledDismissed = ref(false);

const slots = useSlots();
const triggerRef = ref<HTMLElement | null>(null);
const titleRef = ref<HTMLElement | null>(null);
const ancestorEl = ref<HTMLElement | null>(null);
const hasFocusableChild = ref(false);
const hasFocusableAncestor = ref(false);
const hasRenderedTitle = ref(false);
const titleHasInteractive = ref(false);
const hasFocusableElement = computed(
  () => hasFocusableChild.value || hasFocusableAncestor.value,
);
const hasVisibleName = ref(true);

function hasRenderableContent(nodes: VNode[] | undefined): boolean {
  return !!nodes?.some((node) => {
    if (node.type === Comment) return false;
    if (node.type === Text) return String(node.children ?? '').trim() !== '';
    if (node.type === Fragment) {
      return hasRenderableContent(node.children as VNode[]);
    }
    return true;
  });
}

function vnodeText(nodes: VNode[] | undefined): string {
  return (nodes ?? [])
    .map((node) => {
      if (node.type === Comment) return '';
      if (typeof node.children === 'string') return node.children;
      if (Array.isArray(node.children)) {
        return vnodeText(node.children as VNode[]);
      }
      return '';
    })
    .join(' ');
}

// Read from the slot rather than the popup: Ant renders the popup only once it
// first opens, and the popup also holds the CTA button text. Only plain text
// and elements are read; component slots are never called, since a scoped slot
// would run without its arguments.
function titleText() {
  return vnodeText(slots.title?.()).replace(/\s+/g, ' ').trim() || undefined;
}

// The trigger takes focus itself, as a button that toggles the tooltip, only
// when it shows something, there is a tooltip to show, and no control inside or
// around it already takes focus. Called from the template so it re-evaluates on
// every render, including when a conditional slot becomes empty.
function isFocusableTrigger() {
  return (
    props.isTriggerFocusable &&
    !hasFocusableElement.value &&
    hasRenderableContent(slots.default?.()) &&
    hasRenderableContent(slots.title?.())
  );
}

// A trigger is a button only when it has a name: its own content, or the title
// text for an icon-only trigger. Otherwise it keeps a plain tab stop described
// by the popup, as before, rather than an unnamed button.
function triggerAria() {
  if (!isFocusableTrigger()) return {};
  const label = hasVisibleName.value ? undefined : titleText();
  const isButton = hasVisibleName.value || !!label;
  return {
    tabindex: 0,
    role: isButton ? 'button' : undefined,
    'aria-label': label,
    'aria-describedby':
      hasRenderedTitle.value && !label ? tooltipId : undefined,
  };
}
// Only a genuine interactive control (not a focusable region like a tabpanel or
// other [tabindex] container) should absorb the trigger's tab stop and describedby.
const FOCUSABLE_CONTROL_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])';

function firstFocusable(el: HTMLElement | null) {
  return el?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? null;
}

const ROVING_CONTROL_SELECTOR =
  '[role="tab"][tabindex="-1"], [role="menuitem"][tabindex="-1"], [role="menuitemradio"][tabindex="-1"], [role="menuitemcheckbox"][tabindex="-1"], [role="option"][tabindex="-1"], [role="radio"][tabindex="-1"], [role="treeitem"][tabindex="-1"]';

function firstFocusableChild(el: HTMLElement | null) {
  return (
    el?.querySelector<HTMLElement>(
      `${FOCUSABLE_SELECTOR}, ${ROVING_CONTROL_SELECTOR}`,
    ) ?? null
  );
}

function focusableAncestor(el: HTMLElement | null) {
  return (
    el?.parentElement?.closest<HTMLElement>(FOCUSABLE_CONTROL_SELECTOR) ?? null
  );
}

let describedTarget: HTMLElement | null = null;

function syncAccessibility() {
  const focusableChild = firstFocusableChild(triggerRef.value);
  const ancestor = focusableChild ? null : focusableAncestor(triggerRef.value);
  ancestorEl.value = ancestor;
  hasFocusableChild.value = !!focusableChild;
  hasFocusableAncestor.value = !!ancestor;
  hasRenderedTitle.value = !!titleRef.value;
  titleHasInteractive.value = !!firstFocusable(titleRef.value);
  hasVisibleName.value =
    !!triggerRef.value?.textContent?.trim() ||
    !!triggerRef.value?.querySelector('img[alt]:not([alt=""]), [aria-label]');

  const target = focusableChild ?? ancestor;

  // An ancestor target lives outside this component, so drop a stale reference
  // when the target changes or disappears; otherwise it outlives the tooltip id.
  if (
    describedTarget &&
    describedTarget !== target &&
    describedTarget.getAttribute('aria-describedby') === tooltipId
  ) {
    describedTarget.removeAttribute('aria-describedby');
    describedTarget = null;
  }

  if (!target || !slots.title) return;
  const existing = target.getAttribute('aria-describedby');
  if (existing && existing !== tooltipId) return;
  const label = target.getAttribute('aria-label')?.trim();
  const titleText = titleRef.value?.textContent?.trim();
  const labelDuplicatesTitle = !!label && !!titleText && label === titleText;
  if (labelDuplicatesTitle || !hasRenderedTitle.value) {
    if (existing === tooltipId) {
      target.removeAttribute('aria-describedby');
    }
    if (describedTarget === target) describedTarget = null;
  } else {
    target.setAttribute('aria-describedby', tooltipId);
    describedTarget = target;
  }
}

onBeforeUnmount(() => {
  if (describedTarget?.getAttribute('aria-describedby') === tooltipId) {
    describedTarget.removeAttribute('aria-describedby');
  }
  describedTarget = null;
});

onMounted(syncAccessibility);
watch(titleRef, syncAccessibility, { flush: 'post' });
useMutationObserver(triggerRef, syncAccessibility, {
  childList: true,
  subtree: true,
  characterData: true,
  attributes: true,
  attributeFilter: ['disabled'],
});

function getPopupDomNode(): HTMLElement | null {
  const node = tooltipRef.value?.getPopupDomNode();
  return node instanceof HTMLElement ? node : null;
}

const forcedClosed = computed(
  () =>
    isSuppressed.value ||
    escDismissed.value ||
    hoverDismissed.value ||
    controlledDismissed.value,
);

const isOpen = computed(() => {
  if (forcedClosed.value) return false;
  if (props.showTooltip !== null) return props.showTooltip;
  if (!slots.title) return false;
  return isAntdOpen.value || isFocusOpen.value;
});

function overlayContains(node: Node | null) {
  if (!node) return false;
  return (
    !!triggerRef.value?.contains(node) ||
    !!ancestorEl.value?.contains(node) ||
    !!getPopupDomNode()?.contains(node)
  );
}

useEventListener(
  () => (isOpen.value ? document : undefined),
  'keydown',
  (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    if (props.showTooltip !== null) {
      controlledDismissed.value = true;
      return;
    }
    if (overlayContains(document.activeElement)) {
      escDismissed.value = true;
    } else {
      hoverDismissed.value = true;
    }
  },
  { capture: true },
);

let focusFromPointer = false;
function onPointerdown() {
  focusFromPointer = true;
  window.setTimeout(() => {
    focusFromPointer = false;
  }, 0);
}

function onFocusin(event?: FocusEvent) {
  if (focusFromPointer) {
    focusFromPointer = false;
    return;
  }
  if (!slots.title || isSuppressed.value) return;
  const focused = event?.target ?? document.activeElement;
  if (focused instanceof HTMLElement && !focused.matches(':focus-visible')) {
    return;
  }
  if (!isFocusOpen.value) escDismissed.value = false;
  hoverDismissed.value = false;
  controlledDismissed.value = false;
  isFocusOpen.value = true;
}

function onMouseenter() {
  hoverDismissed.value = false;
  controlledDismissed.value = false;
}

function onMouseleave() {
  hoverDismissed.value = false;
  if (!isFocusOpen.value) escDismissed.value = false;
}

// A focusable trigger has role="button", so Enter and Space must do something:
// they toggle the tooltip.
function onTriggerKeydown(event: KeyboardEvent) {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  if (
    event.target !== triggerRef.value ||
    triggerRef.value?.getAttribute('role') !== 'button'
  ) {
    return;
  }
  event.preventDefault();
  if (props.showTooltip !== null) return;
  if (isOpen.value) {
    escDismissed.value = true;
    return;
  }
  escDismissed.value = false;
  hoverDismissed.value = false;
  isFocusOpen.value = true;
}

useEventListener(
  () => (isFocusOpen.value ? document : undefined),
  'focusout',
  () =>
    window.setTimeout(() => {
      if (overlayContains(document.activeElement)) return;
      isFocusOpen.value = false;
      escDismissed.value = false;
    }, 0),
  { capture: true },
);

useEventListener(() => ancestorEl.value, 'pointerdown', onPointerdown);
useEventListener(() => ancestorEl.value, 'focusin', onFocusin);

const { effectivePlacement, getPopupContainer, onShow, onHide } =
  useViewportPlacement(
    toRef(props, 'placement'),
    toRef(props, 'mountToBody'),
    getPopupDomNode,
  );

function onVisibleChange(visible: boolean) {
  isAntdOpen.value = visible;
}

watch(
  isOpen,
  (open, wasOpen) => {
    if (wasOpen !== undefined && open !== wasOpen) {
      emit('menu-visible', { visible: open });
    }
    open ? onShow() : onHide();
  },
  { immediate: true },
);

const effectiveAlign = computed(() =>
  props.mountToBody ? withViewportOverflow(props.align) : props.align,
);

const dynamicProps = computed(() => {
  if (forcedClosed.value) return { open: false };
  if (props.showTooltip !== null) return { open: props.showTooltip };
  if (isFocusOpen.value && slots.title) return { open: true };
  return {};
});

function onCtaClicked(event: Event) {
  if (props.stopPropagation) event.stopPropagation();
  isFocusOpen.value = false;
  isSuppressed.value = true;
  emit('cta-clicked');
  window.setTimeout(() => {
    isSuppressed.value = false;
  }, 300);
}
</script>

<style lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.c-bitts-tooltip__button {
  @apply w-full my-4;
}
.bitts-tooltip {
  .ant-tooltip {
    font-family: inherit;
    max-width: 280px !important;
  }
  .ant-tooltip-inner {
    @apply text-sm min-h-0 px-8 py-4 bg-neutral-background-tooltip rounded-bts-md;
    opacity: 90% !important;
  }
  &.ant-tooltip-placement-top,
  &.ant-tooltip-placement-topLeft,
  &.ant-tooltip-placement-topRight {
    padding-bottom: 0;
  }
  &.ant-tooltip-placement-right,
  &.ant-tooltip-placement-rightTop,
  &.ant-tooltip-placement-rightBottom {
    padding-left: 0;
  }
  &.ant-tooltip-placement-bottom,
  &.ant-tooltip-placement-bottomLeft,
  &.ant-tooltip-placement-bottomRight {
    padding-top: 0;
  }
  &.ant-tooltip-placement-left,
  &.ant-tooltip-placement-leftTop,
  &.ant-tooltip-placement-leftBottom {
    padding-right: 0;
  }
}
</style>
